import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import swiftxLogo from '../../central/delivery-monitor/images/swiftx-logo.png';
import { pacificTaskDate, toMotionPoint } from '../../central/delivery-monitor/deliveryMonitorMotion';
import { QUERY_PICKUP_DRIVER_TRACKING_TRAJECTORY } from '../../../graphql/queries/pickupDriverTracking/queryPickupDriverTracking';
import type { QueryPickupDriverTrackingTrajectoryResponse } from '../../../graphql/types/pickupDriverTracking/PickupDriverTrackingTypes';
import { MapPanelHandle, TrackingMapPanel } from './TrackingMapPanel';
import { TrackingDriverList } from './TrackingDriverList';
import { TrackingDriverPopup } from './TrackingDriverPopup';
import { TrackingKpiRow } from './TrackingKpiRow';
import { TrackingSearchBox } from './TrackingSearchBox';
import { TrackingAsOfModal } from './TrackingAsOfModal';
import { alertCount, kingKey, vehicleCounts, visibleDrivers } from './pickupTrackingLogic';
import type { SortDir, VehicleFilter } from './pickupTrackingLogic';
import type {
    TrackingDriver,
    TrackingFeedEvent,
    TrackingSearchHit,
    TrackingSearchScope,
    TrackingSortKey,
} from './pickupTrackingTypes';
import { useCountTween } from './useCountTween';
import { PICKUP_POLL_POSITIONS_MS, usePickupTrackingData } from './usePickupTrackingData';
import type { ReplayRequest } from './useStopReplay';
import { useTrackingFeed } from './useTrackingFeed';
import styles from './PickupDriverTrackingPage.module.css';

// 大屏对齐 1920×1080 底稿，横纵独立缩放铺满（同派送大屏）
const STAGE_W = 1920;
const STAGE_H = 1080;
const PACIFIC_TZ = 'America/Los_Angeles';
const TOAST_MS = 2400;
const KPI_STORAGE_PREFIX = 'pickup-driver-tracking:kpi:';
const MINUTE_MS = 60_000;

const useNow = (): Date => {
    const [now, setNow] = useState<Date>(() => new Date());
    useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), 1000);
        return () => window.clearInterval(id);
    }, []);
    return now;
};

const PickupDriverTrackingPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.resolvedLanguage || i18n.language || 'en';
    const isZh = lang.startsWith('zh');
    const rootRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<MapPanelHandle>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [stageScale, setStageScale] = useState<{ x: number; y: number }>({ x: 1, y: 1 });

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const compute = () => {
            const sx = el.clientWidth / STAGE_W;
            const sy = el.clientHeight / STAGE_H;
            setStageScale({ x: sx > 0 ? sx : 1, y: sy > 0 ? sy : 1 });
        };
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // —— toast —— //
    const [toastMsg, setToastMsg] = useState('');
    const [toastOn, setToastOn] = useState(false);
    const toastTimer = useRef<number | null>(null);
    const showToast = useCallback((msg: string) => {
        if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
        setToastMsg(msg);
        setToastOn(true);
        toastTimer.current = window.setTimeout(() => setToastOn(false), TOAST_MS);
    }, []);
    useEffect(
        () => () => {
            if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
        },
        [],
    );

    // —— 全屏 / 语言 / 时钟 —— //
    const toggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            // 浏览器拒绝全屏时与原型一致，toast「敬请期待」
            rootRef.current?.requestFullscreen().catch(() => showToast(t('pickup.driverTracking.soon')));
        }
    }, [showToast, t]);
    const toggleLanguage = useCallback(() => {
        i18n.changeLanguage(isZh ? 'en' : 'zh');
    }, [i18n, isZh]);

    const now = useNow();
    // 指定查询时刻（同派送大屏）：从 baseMs 起跟真实时间一起走（setAtMs = 设定时的真实时间）；null = 实时
    const [asOfAnchor, setAsOfAnchor] = useState<{ baseMs: number; setAtMs: number } | null>(null);
    const [asOfOpen, setAsOfOpen] = useState(false);
    const asOfElapsedMs = asOfAnchor !== null ? Math.max(0, now.getTime() - asOfAnchor.setAtMs) : 0;
    const clockDate = useMemo(
        () => (asOfAnchor !== null ? new Date(asOfAnchor.baseMs + asOfElapsedMs) : now),
        [asOfAnchor, asOfElapsedMs, now],
    );
    // 位置查询的指定时刻按位置轮询周期步进：每 20s 变一次，变量变化即触发位置刷新，小车随之移动
    const asOfMs =
        asOfAnchor !== null
            ? asOfAnchor.baseMs + Math.floor(asOfElapsedMs / PICKUP_POLL_POSITIONS_MS) * PICKUP_POLL_POSITIONS_MS
            : null;
    // 快照的指定时刻取整到分钟：每分钟才变一次变量，后端缓存也按分钟命中
    const asOfMinuteMs = asOfMs !== null ? asOfMs - (asOfMs % MINUTE_MS) : null;
    const asOfBaseMs = asOfAnchor?.baseMs ?? null;
    // 任务日取美西日期：实时跟 now、指定查询时刻跟 asOfMs 走，跨美西零点自动切日
    const taskDate = pacificTaskDate(asOfMs ?? now.getTime());

    // 大屏统一按美西时间显示（不跟随浏览器时区）
    const timeText = useMemo(
        () =>
            new Intl.DateTimeFormat('en-GB', {
                timeZone: PACIFIC_TZ,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23',
            }).format(clockDate),
        [clockDate],
    );
    const dateText = useMemo(
        () =>
            new Intl.DateTimeFormat(isZh ? 'zh-CN' : 'en-US', {
                timeZone: PACIFIC_TZ,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                weekday: 'short',
            }).format(clockDate),
        [clockDate, isZh],
    );

    // —— 数据 —— //
    const feedRef = useRef<ReturnType<typeof useTrackingFeed> | null>(null);
    const feedBody = (e: TrackingFeedEvent) =>
        t(e.fail ? 'pickup.driverTracking.feedFail' : 'pickup.driverTracking.feedOk');
    // 数据一批批来，排队摊匀弹出
    const onPickupEvents = (events: TrackingFeedEvent[], spanMs: number) => {
        feedRef.current?.enqueue(
            events.map((e) => ({ e, body: feedBody(e) })),
            spanMs,
        );
    };
    const { snapshot, kpi, positionsLoading, positionCount } = usePickupTrackingData({
        enabled: true,
        // hub 只跟随左侧布局的 hub 选择器
        hubId: null,
        taskDate,
        asOfMs,
        asOfMinuteMs,
        asOfBaseMs,
        onPickupEvents,
    });
    // 换 hub / 换业务日 / 换指定查询时刻都清空 feed
    const scopeKey = snapshot ? `${snapshot.hubId}|${taskDate}|${asOfBaseMs ?? ''}` : null;
    const feedApi = useTrackingFeed(scopeKey);
    feedRef.current = feedApi;
    const { feed } = feedApi;

    const drivers = useMemo(() => snapshot?.drivers ?? [], [snapshot]);
    const hub = snapshot?.hub ?? null;
    const thresholdMin = snapshot?.longStayThresholdMin ?? 0;
    const positionPollMs = snapshot?.positionPollMs ?? PICKUP_POLL_POSITIONS_MS;

    // KPI 补间：首屏数字从 0 / 上一 slot 起走；KPI 不等快照，各自回来各自显示（同派送大屏）。
    // 基线 key 带业务日（同派送大屏）：跨美西零点后新一天从头补间，不被昨天的终值按 max 压住
    const kpiScopeKey = kpi ? `${kpi.hubId}|${taskDate}|${asOfBaseMs ?? ''}` : null;
    const shownKpi = useCountTween(
        kpi?.history.current,
        kpi?.history.previous,
        kpi
            ? `${KPI_STORAGE_PREFIX}${taskDate}:${kpi.hubId}${asOfBaseMs !== null ? `:asof:${asOfBaseMs}` : ''}`
            : null,
        kpi?.slotMs ?? 60_000,
    );
    // 清掉非当天的基线（同派送大屏），sessionStorage 不随天数累积
    useEffect(() => {
        try {
            const currentPrefix = `${KPI_STORAGE_PREFIX}${taskDate}:`;
            for (let i = sessionStorage.length - 1; i >= 0; i--) {
                const k = sessionStorage.key(i);
                if (k && k.startsWith(KPI_STORAGE_PREFIX) && !k.startsWith(currentPrefix)) {
                    sessionStorage.removeItem(k);
                }
            }
        } catch {
            // ignore
        }
    }, [taskDate]);

    // —— 筛选 / 排序 / 选中 —— //
    const [vehicle, setVehicle] = useState<VehicleFilter>('all');
    const [alertOnly, setAlertOnly] = useState(false);
    const [sortKey, setSortKey] = useState<TrackingSortKey>('rate');
    const [sortDir, setSortDir] = useState<SortDir>(-1);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [replay, setReplay] = useState<ReplayRequest | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [pendingFly, setPendingFly] = useState<string | null>(null);
    const [replayLoading, setReplayLoading] = useState(false);
    const runIdRef = useRef(0);
    const selectedKeyRef = useRef(selectedKey);
    selectedKeyRef.current = selectedKey;
    const client = useApolloClient();

    const listed = useMemo(
        () => visibleDrivers(drivers, vehicle, alertOnly, sortKey, sortDir),
        [drivers, vehicle, alertOnly, sortKey, sortDir],
    );
    const counts = useMemo(() => vehicleCounts(drivers), [drivers]);
    const alerts = useMemo(() => alertCount(drivers), [drivers]);
    const vehicleAlerts = useMemo(() => alertCount(drivers, vehicle), [drivers, vehicle]);
    const king = useMemo(() => kingKey(drivers), [drivers]);
    const selectedDriver = useMemo(
        () => drivers.find((d) => d.key === selectedKey) ?? null,
        [drivers, selectedKey],
    );
    const activeReplay = replay && drivers.some((d) => d.key === replay.key) ? replay : null;
    const replayDriver = activeReplay ? drivers.find((d) => d.key === activeReplay.key) ?? null : null;

    const deselect = useCallback(() => {
        setSelectedKey(null);
        setReplay(null);
    }, []);

    // 左侧切 hub 时清空选中 / 回放；快照加载中（hubId 为 null）不算切换
    const snapshotHubId = snapshot?.hubId ?? null;
    const lastHubIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (snapshotHubId === null) return;
        if (lastHubIdRef.current !== null && lastHubIdRef.current !== snapshotHubId) deselect();
        lastHubIdRef.current = snapshotHubId;
    }, [snapshotHubId, deselect]);

    const select = useCallback((d: TrackingDriver, fromMap: boolean) => {
        setReplay(null);
        setSelectedKey(d.key);
        if (!fromMap) mapRef.current?.panIfOutside(d.key);
    }, []);

    // 同派送大屏 handleReplay：点「当日轨迹」/ 搜索打开时才查一次 ClickHouse 轨迹，回放中不再刷新
    const hubIdForQuery = snapshot?.hubId ?? null;
    const playRoute = useCallback(
        async (d: TrackingDriver, focus: ReplayRequest['focus']) => {
            if (hubIdForQuery === null) return;
            runIdRef.current += 1;
            const runId = runIdRef.current;
            // 同派送 replay.stop()：回放中搜索另一司机时先退出旧回放，查不到点时停在新司机弹窗上
            setReplay(null);
            setReplayLoading(true);
            try {
                const { data } = await client.query<QueryPickupDriverTrackingTrajectoryResponse>({
                    query: QUERY_PICKUP_DRIVER_TRACKING_TRAJECTORY,
                    variables: { hubId: Number(hubIdForQuery), driverAccountId: Number(d.key), taskDate, asOfMs },
                    fetchPolicy: 'network-only',
                });
                // null = CH 未配置；[] = 今日无点。两种都保持弹窗打开，只提示
                const points = data?.pickupDriverTrackingTrajectory;
                if (points === null || points === undefined) {
                    showToast(t('pickup.driverTracking.trajectoryUnavailable'));
                    return;
                }
                if (points.length === 0) {
                    showToast(t('pickup.driverTracking.trajectoryEmpty'));
                    return;
                }
                // 等待期间用户关了弹窗、切到别的司机或又触发了一次，就不再回放
                if (runIdRef.current !== runId || selectedKeyRef.current !== d.key) return;
                setReplay({ key: d.key, runId, focus, points: points.map(toMotionPoint) });
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[pickup-driver-tracking] trajectory query failed', err);
                showToast(t('pickup.driverTracking.trajectoryUnavailable'));
            } finally {
                if (runIdRef.current === runId) setReplayLoading(false);
            }
        },
        [hubIdForQuery, client, taskDate, asOfMs, showToast, t],
    );

    // 搜索 / feed 命中的司机被当前筛选挡住时，先清筛选（原型 searchPick）
    const clearFiltersIfHidden = useCallback(
        (d: TrackingDriver) => {
            if ((vehicle !== 'all' && vehicle !== d.vehicleType) || (alertOnly && d.anomalies.length === 0)) {
                setVehicle('all');
                setAlertOnly(false);
            }
        },
        [vehicle, alertOnly],
    );

    const locateDriver = useCallback(
        (d: TrackingDriver) => {
            clearFiltersIfHidden(d);
            select(d, true);
            // 小车 host 要等筛选清掉后的这一轮渲染才存在，等 effect 再飞
            setPendingFly(d.key);
        },
        [clearFiltersIfHidden, select],
    );

    useEffect(() => {
        if (pendingFly === null) return;
        mapRef.current?.flyToDriver(pendingFly);
        setPendingFly(null);
    }, [pendingFly, listed]);

    const onSearchPick = useCallback(
        (hit: TrackingSearchHit, scope: TrackingSearchScope) => {
            setSearchOpen(false);
            if (hit.stopIdx === null) {
                locateDriver(hit.driver);
                return;
            }
            clearFiltersIfHidden(hit.driver);
            setSelectedKey(hit.driver.key);
            playRoute(hit.driver, { idx: hit.stopIdx, scope });
        },
        [clearFiltersIfHidden, locateDriver, playRoute],
    );

    const onFeedPick = useCallback(
        (driverKey: string) => {
            const d = drivers.find((x) => x.key === driverKey);
            if (d) locateDriver(d);
        },
        [drivers, locateDriver],
    );

    const onVehicleChange = useCallback(
        (v: VehicleFilter) => {
            setVehicle(v);
            if (selectedDriver && v !== 'all' && selectedDriver.vehicleType !== v) deselect();
        },
        [selectedDriver, deselect],
    );

    const onToggleAlert = useCallback(() => {
        const next = !alertOnly;
        setAlertOnly(next);
        if (next && selectedDriver && selectedDriver.anomalies.length === 0) deselect();
    }, [alertOnly, selectedDriver, deselect]);

    const onSortClick = useCallback(
        (k: TrackingSortKey) => {
            if (sortKey === k) {
                setSortDir((d) => (d === -1 ? 1 : -1));
            } else {
                setSortKey(k);
                setSortDir(-1);
            }
        },
        [sortKey],
    );

    const closeMenus = useCallback(() => {
        setSearchOpen(false);
    }, []);

    const popupDriver = selectedDriver && !activeReplay ? selectedDriver : null;
    // 地图上有没有这辆车只有地图面板知道（host 在它那里），由它渲染时传入 canLocate（同派送大屏）
    const popup = popupDriver
        ? (canLocate: boolean) => (
              <TrackingDriverPopup
                  driver={popupDriver}
                  thresholdMin={thresholdMin}
                  popupRef={popupRef}
                  onClose={deselect}
                  onMessage={() => showToast(t('pickup.driverTracking.soon'))}
                  canLocate={canLocate}
                  onLocate={() => mapRef.current?.locate(popupDriver.key)}
                  replayLoading={replayLoading}
                  onReplay={() => playRoute(popupDriver, null)}
              />
          )
        : null;

    return (
        <div ref={rootRef} className={`flex-1 min-h-0 -m-6 ${styles.root}`} onClick={closeMenus}>
            <div
                className={`${styles.stage} ${styles.booted}`}
                style={{
                    transform: `translate(-50%, -50%) scale(${stageScale.x}, ${stageScale.y})`,
                }}
            >
                <header className={styles.hdr}>
                    <div className={styles.brand}>
                        <img src={swiftxLogo} alt="SwiftX" className={styles.logo} />
                        <div>
                            <h1 className={styles.brandTitle}>{t('pickup.driverTracking.title')}</h1>
                        </div>
                    </div>
                    <TrackingSearchBox
                        drivers={drivers}
                        open={searchOpen}
                        onOpenChange={setSearchOpen}
                        onPick={onSearchPick}
                    />
                    <div style={{ flex: 1 }} />
                    {asOfMs === null && (
                        <div className={styles.live}>
                            <i />
                            <span>{t('pickup.driverTracking.live')}</span>
                        </div>
                    )}
                    <div
                        className={styles.clock}
                        onClick={(e) => {
                            // 隐藏入口：三连击打开「指定查询时刻」
                            if (e.detail !== 3) return;
                            window.getSelection()?.removeAllRanges();
                            setAsOfOpen(true);
                        }}
                    >
                        <div className={`${styles.clockTime} ${styles.num}`}>
                            {timeText}
                            <span className={styles.clockTz}>{t('pickup.driverTracking.clock.tzLabel')}</span>
                        </div>
                        <div className={styles.clockDate}>{dateText}</div>
                    </div>
                    <button
                        type="button"
                        className={styles.hbtn}
                        onClick={toggleLanguage}
                        aria-label={t('pickup.driverTracking.switchLanguage') || ''}
                    >
                        {isZh ? 'EN' : '中'}
                    </button>
                    <button
                        type="button"
                        className={styles.hbtn}
                        onClick={toggleFullscreen}
                        aria-label={t('pickup.driverTracking.fullscreen') || ''}
                    >
                        ⛶
                    </button>
                </header>

                <TrackingKpiRow
                    shown={shownKpi}
                    current={kpi?.history.current ?? null}
                    yesterday={kpi?.history.yesterday ?? null}
                    alertCount={alerts}
                    alertOnly={alertOnly}
                    onToggleAlert={onToggleAlert}
                    resetKey={kpiScopeKey}
                />

                <main className={styles.main}>
                    <TrackingMapPanel
                        ref={mapRef}
                        hub={hub}
                        drivers={listed}
                        scopeKey={scopeKey}
                        positionsLoading={positionsLoading}
                        positionCount={positionCount}
                        kingKey={king}
                        selectedKey={selectedKey}
                        positionPollMs={positionPollMs}
                        thresholdMin={thresholdMin}
                        replay={activeReplay}
                        replayDriver={replayDriver}
                        popupDriverKey={popupDriver?.key ?? null}
                        popupRef={popupRef}
                        popup={popup}
                        feed={feed}
                        onSelectDriver={(d) => select(d, true)}
                        onBlankClick={() => {
                            if (selectedKey) deselect();
                        }}
                        onExitReplay={() => setReplay(null)}
                        onFeedPick={onFeedPick}
                    />
                    <TrackingDriverList
                        drivers={listed}
                        loading={snapshot === null}
                        vehicleCounts={counts}
                        alertCount={vehicleAlerts}
                        vehicle={vehicle}
                        alertOnly={alertOnly}
                        sortKey={sortKey}
                        sortDir={sortDir}
                        selectedKey={selectedKey}
                        onVehicleChange={onVehicleChange}
                        onSortClick={onSortClick}
                        onToggleAlert={onToggleAlert}
                        onFlipDir={() => setSortDir((d) => (d === -1 ? 1 : -1))}
                        onSelect={(d) => select(d, false)}
                    />
                </main>

                <div className={`${styles.toast} ${toastOn ? styles.toastShow : ''}`} role="status">
                    <i />
                    <span>{toastMsg}</span>
                </div>
            </div>

            <TrackingAsOfModal
                open={asOfOpen}
                asOfMs={asOfBaseMs}
                getContainer={() => rootRef.current ?? document.body}
                onConfirm={(ms) => {
                    setAsOfAnchor({ baseMs: ms, setAtMs: Date.now() });
                    setAsOfOpen(false);
                    setReplay(null);
                }}
                onReset={() => {
                    setAsOfAnchor(null);
                    setAsOfOpen(false);
                    setReplay(null);
                }}
                onClose={() => setAsOfOpen(false)}
            />
        </div>
    );
};

export default PickupDriverTrackingPage;
